"use client";

import type {
  ColumnFiltersState,
  ColumnSort,
  OnChangeFn,
  PaginationState,
  RowSelectionState,
  SortingState,
} from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { parseAsIndex, parseAsInteger, useQueryStates } from "../../../adapters";

interface UseTableStateOptions {
  defaultSort?: ColumnSort;
  defaultPageSize?: number;
  defaultPageIndex?: number;
  defaultFilters?: ColumnFiltersState;
  defaultRowSelection?: RowSelectionState;
  sortParam?: string;
  pageParam?: string;
  pageSizeParam?: string;
  filterParam?: string;
  shouldUseParams?: boolean;
}

interface UseTableStateReturn {
  sorting: SortingState;
  pagination: PaginationState;
  columnFilters: ColumnFiltersState;
  rowSelection: RowSelectionState;
  onSortingChange: OnChangeFn<SortingState>;
  onPaginationChange: OnChangeFn<PaginationState>;
  onColumnFiltersChange: OnChangeFn<ColumnFiltersState>;
  onRowSelectionChange: OnChangeFn<RowSelectionState>;
  /** Array of selected row IDs for convenience */
  selectedRowIds: string[];
  /** Clear all row selections */
  clearRowSelection: () => void;
  sortDirection: "asc" | "desc";
  serializedSort: string;
  resetParams: Record<string, unknown>;
  tableQueryParsers: Record<string, unknown>; // The parsers for useQueryStates
}

// Custom serializers for nuqs, so that we can use strings in the URL and convert them
// to SortingState, ColumnFiltersState, and PaginationState for tanstack table.

const sortingSerializer = {
  // Converts a string like "-createdAt" to a SortingState like [{ id: "createdAt", desc: true }]
  parse: (value: string): SortingState | null => {
    if (!value) return [];
    return value.split(",").map((field) => {
      const desc = field.startsWith("-");
      const id = desc ? field.slice(1) : field;
      return { id, desc };
    });
  },
  // Converts a SortingState like [{ id: "createdAt", desc: true }] to a string like "-createdAt"
  serialize: (sorting: SortingState): string => sorting.map(({ id, desc }) => (desc ? `-${id}` : id)).join(","),
};

// Converts a JSON string like '[{"id":"createdAt","value":1}]' to a ColumnFiltersState
// Note: nuqs handles URL encoding/decoding automatically, so we only need JSON parse/stringify
const filtersSerializer = {
  parse: (value: string): ColumnFiltersState | null => {
    if (!value) return [];
    try {
      return JSON.parse(value);
    } catch {
      return [];
    }
  },
  // Converts a ColumnFiltersState like [{ id: "createdAt", value: 1 }] to a JSON string
  serialize: (filters: ColumnFiltersState): string => JSON.stringify(filters),
};

export function useTableState({
  defaultSort,
  defaultPageSize = 50,
  defaultPageIndex = 0,
  defaultFilters = [],
  defaultRowSelection = {},
  sortParam = "sort",
  pageParam = "page[number]",
  pageSizeParam = "page[size]",
  filterParam = "filter",
  shouldUseParams = true,
}: UseTableStateOptions = {}): UseTableStateReturn {
  // Local state fallbacks to support shouldUseParams=false
  const [localSorting, setLocalSorting] = useState<SortingState>(defaultSort ? [defaultSort] : []);
  const [localPagination, setLocalPagination] = useState<PaginationState>({
    pageIndex: defaultPageIndex,
    pageSize: defaultPageSize,
  });
  const [localFilters, setLocalFilters] = useState<ColumnFiltersState>(defaultFilters);
  // Row selection is always local state (not persisted to URL as it can be large)
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(defaultRowSelection);

  // nuqs-managed URL state for atomic updates across keys: https://nuqs.47ng.com/docs/batching#usequerystates
  const [queryState, setQueryState] = useQueryStates(
    {
      [sortParam]: sortingSerializer,
      [pageParam]: parseAsIndex.withDefault(defaultPageIndex), // parseAsIndex handles 1-based URL conversion
      [pageSizeParam]: parseAsInteger.withDefault(defaultPageSize),
      [filterParam]: filtersSerializer,
    },
    {
      shallow: true,
      history: "replace",
    },
  );

  const sorting: SortingState = useMemo(
    () =>
      shouldUseParams
        ? ((queryState[sortParam] as SortingState | null) ?? (defaultSort ? [defaultSort] : []))
        : localSorting,
    [shouldUseParams, queryState, sortParam, defaultSort, localSorting],
  );

  const pagination: PaginationState = useMemo(
    () =>
      shouldUseParams
        ? {
            // Clamp the pageIndex to at least 0
            pageIndex: Math.max(0, (queryState[pageParam] as number | null) ?? defaultPageIndex),
            // Clamp the pageSize to at least 1
            pageSize: Math.max(1, (queryState[pageSizeParam] as number | null) ?? defaultPageSize),
          }
        : localPagination,
    [shouldUseParams, queryState, pageParam, pageSizeParam, defaultPageIndex, defaultPageSize, localPagination],
  );

  const columnFilters: ColumnFiltersState = useMemo(
    () => (shouldUseParams ? ((queryState[filterParam] as ColumnFiltersState | null) ?? defaultFilters) : localFilters),
    [shouldUseParams, queryState, filterParam, defaultFilters, localFilters],
  );

  const onSortingChange: OnChangeFn<SortingState> = shouldUseParams
    ? (updater) => {
        // updater is a function that takes the current sorting and returns the new sorting
        const newSorting = typeof updater === "function" ? updater(sorting) : updater;
        setQueryState({
          // Set the sortParam to the new sorting
          [sortParam]: newSorting.length ? newSorting : null,
          // Reset page to first page (0-based) when sorting changes
          [pageParam]: 0,
        });
      }
    : (updater) => {
        setLocalSorting((prev) => (typeof updater === "function" ? updater(prev) : updater));
        // Also reset local pagination pageIndex
        setLocalPagination((prev) => ({ ...prev, pageIndex: 0 }));
      };

  const onPaginationChange: OnChangeFn<PaginationState> = shouldUseParams
    ? (updater) => {
        // updater is a function that takes the current pagination and returns the new pagination
        const newPagination = typeof updater === "function" ? updater(pagination) : updater;
        setQueryState({
          [pageParam]: newPagination.pageIndex,
          [pageSizeParam]: newPagination.pageSize,
        });
      }
    : (updater) => setLocalPagination((prev) => (typeof updater === "function" ? updater(prev) : updater));

  const onColumnFiltersChange: OnChangeFn<ColumnFiltersState> = shouldUseParams
    ? (updater) => {
        const newFilters = typeof updater === "function" ? updater(columnFilters) : updater;
        return setQueryState({
          [filterParam]: newFilters.length ? newFilters : null,
          // Reset page to first page (0-based) when filters change
          [pageParam]: 0,
        });
      }
    : (updater) => {
        setLocalFilters((prev) => (typeof updater === "function" ? updater(prev) : updater));
        setLocalPagination((prev) => ({ ...prev, pageIndex: 0 }));
      };

  // Row selection handler - always uses local state (not URL persisted)
  const onRowSelectionChange: OnChangeFn<RowSelectionState> = useCallback((updater) => {
    setRowSelection((prev) => (typeof updater === "function" ? updater(prev) : updater));
  }, []);

  // Convenience helper: array of selected row IDs
  const selectedRowIds = useMemo(() => Object.keys(rowSelection).filter((key) => rowSelection[key]), [rowSelection]);

  // Convenience helper: clear all selections
  const clearRowSelection = useCallback(() => {
    setRowSelection({});
  }, []);

  const sortDirection = sorting.length ? (sorting[0].desc ? "desc" : "asc") : "asc";
  const serializedSort = sorting.length ? `${sorting[0].desc ? "-" : ""}${sorting[0].id}` : "";

  // Expose the parsers for creating parallel useQueryStates
  // These are the SAME parsers useTableState uses internally
  const tableQueryParsers = {
    [sortParam]: sortingSerializer,
    [pageParam]: parseAsIndex.withDefault(defaultPageIndex),
    [pageSizeParam]: parseAsInteger.withDefault(defaultPageSize),
    [filterParam]: filtersSerializer,
  };

  // Expose the reset values for atomic updates
  const resetParams = {
    [sortParam]: defaultSort ? [defaultSort] : null,
    [filterParam]: defaultFilters.length ? defaultFilters : null,
    [pageParam]: defaultPageIndex,
    [pageSizeParam]: defaultPageSize,
  };

  return {
    sorting,
    pagination,
    columnFilters,
    rowSelection,
    onSortingChange,
    onPaginationChange,
    onColumnFiltersChange,
    onRowSelectionChange,
    selectedRowIds,
    clearRowSelection,
    sortDirection,
    serializedSort,
    resetParams,
    tableQueryParsers,
  };
}
